<script setup lang="ts">
import { TriangleAlert } from 'lucide-vue-next'
import { Loader2 } from 'lucide-vue-next'

import type { RadioCardOption as RadioOption } from '@/components/ui/AppRadioCard.vue'

const props = withDefaults(defineProps<{ loadOnMount?: boolean }>(), { loadOnMount: true })

const emit = defineEmits<{
  (e: 'saved'): void
}>()

const { t } = useI18n()
const {
  fillWebappRegistrationTexts,
  isPolite,
  getMerchantTexts,
  merchantTextsForm,
  patchMerchantTextsFromTabletForm,
  formalDefaultTexts,
  nonFormalDefaultTexts
} = useMerchantTexts()

const formal = ref(isPolite)
const radioValues = <RadioOption[]>[
  {
    title: t('pages.loyaltyPage.tabs.myTexts.formal.radio.first.title'),
    description: t('pages.loyaltyPage.tabs.myTexts.formal.radio.first.description'),
    value: false
  },
  {
    title: t('pages.loyaltyPage.tabs.myTexts.formal.radio.second.title'),
    description: t('pages.loyaltyPage.tabs.myTexts.formal.radio.second.description'),
    value: true
  }
]

const handleTextAddressing = async () => {
  merchantTextsForm.value = formal.value
    ? { ...formalDefaultTexts, languageTone: { usePoliteLanguage: { value: true } } }
    : { ...nonFormalDefaultTexts, languageTone: { usePoliteLanguage: { value: false } } }

  await patchMerchantTextsFromTabletForm(false)
}

const isOpen = ref(false)

const selectedFormal = computed<RadioOption | undefined>({
  get: () => radioValues.find((r) => r.value === formal.value),
  set: (opt) => {
    if (opt?.value === formal.value) return
    formal.value = opt?.value as boolean
    isOpen.value = true
  }
})

const handleCancel = () => {
  formal.value = !formal.value
  isOpen.value = false
}

const updateMerchantTexts = async () => {
  const data = await getMerchantTexts()

  merchantTextsForm.value.webappTextsFields.webappHomeTitle.value = data.webappHomeTitle
  merchantTextsForm.value.webappTextsFields.webappHomeSubtitle.value = data.webappHomeSubtitle
  merchantTextsForm.value.languageTone.usePoliteLanguage.value = data.usePoliteLanguage
  fillWebappRegistrationTexts(data)
}

const loadingSave = ref(false)
const handleSave = async () => {
  loadingSave.value = true
  await handleTextAddressing()
  setTimeout(async () => {
    await updateMerchantTexts()
    loadingSave.value = false
    isOpen.value = false
    emit('saved')
  }, 1000)
}
onBeforeMount(async () => {
  if (props.loadOnMount) await updateMerchantTexts()
})
</script>

<template>
  <div>
    <AppRadioCard v-model="selectedFormal" :options="radioValues" class="grid grid-cols-2 gap-5" />

    <Dialog v-model:open="isOpen">
      <DialogContent :show-close-button="false" class="sm:max-w-lg">
        <DialogHeader>
          <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <TriangleAlert class="h-6 w-6 text-red-600" />
          </div>
          <DialogTitle class="mt-3 text-center sm:mt-5">
            {{ t('pages.loyaltySupports.merchantTextFormalSave.title') }}
          </DialogTitle>
          <DialogDescription class="text-center">
            {{ t('pages.loyaltySupports.merchantTextFormalSave.subtitle') }}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter class="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
          <Button variant="ghost" :disabled="loadingSave" class="cursor-pointer" @click="handleCancel">
            {{ t('pages.loyaltySupports.merchantTextFormalSave.cancel') }}
          </Button>
          <Button variant="default-primary" :disabled="loadingSave" @click="handleSave">
            <Loader2 v-if="loadingSave" class="animate-spin" />
            {{ t('pages.loyaltySupports.merchantTextFormalSave.confirm') }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
